import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import PageHeader from 'components/global/PageHeader';
import Tooltip from 'components/global/Tooltip';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useAccessControl from 'hooks/useAccessControl';
import useAppTracking from 'hooks/useAppTracking';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import { BRAND } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faEye, faPlus, faRectangleAd } from '@fortawesome/pro-solid-svg-icons';

import ContentPreferences from '../ContentPreferences/ContentPreferences';

const CustomizablePagesList = () => {
	const isRestricted = useRestriction();
	const isAuthorized = useAccessControl();
	const { showModal } = useModalRouter();
	const { track } = useAppTracking();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const defaultSort = useMemo(() => [{ id: 'ID', desc: true }], []);
	const navigate = useNavigate();

	const customizablePagesCount = useSelector((state) => state.dynamicListData[DYNAMICLISTS.CUSTOMIZABLE_PAGES]?.size);

	const isAddButtonDisabled = isRestricted(SYSTEM_LIMITS.CUSTOMIZABLE_PAGE, customizablePagesCount);
	const columns = useMemo(
		() => [
			{ Header: 'id', accessor: 'ID', filterType: FilterType.string, show: false },
			{
				Header: 'messageType',
				accessor: 'smsType',
				size: 50,
				filterType: FilterType.multiSelection,
				showOnMobile: true
			},
			{
				Header: 'brandName',
				accessor: 'brandName',
				filterType: FilterType.multiSelection,
				renderCell: ListComponents.BRAND_LOGO,
				roleName: 'BrandingModule',
				showOnMobile: true,
				size: 50
			},
			{ Header: 'language', accessor: 'language', filterType: FilterType.multiSelection }
		],
		[]
	);

	return (
		<div>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/customizable-pages-icon.json.gz"
				title="customizablePages"
				tooltip="customizablePages.header.description">
				{!isAddButtonDisabled && isAuthorized('SMSModule') && (
					<Button
						icon={faRectangleAd}
						label={!isMobile && 'contentPreferences'}
						onClick={() => NiceModal.show(ContentPreferences)}
						variant="black"
					/>
				)}
				<Authorization path="/modal/customizable-page-form">
					{isAddButtonDisabled ? (
						<Tooltip
							timeout={100}
							placement="bottom"
							color="var(--white)"
							title={
								<div style={{ display: 'block' }}>
									<ModuleToolTipContainer
										infoImage="lottie_smsLottie"
										title="smsModule"
										description="smsModuleSubtitle"
									/>

									<Button
										style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
										className="mt-3"
										label="showPlans"
										icon={faEye}
										variant="blue"
										onClick={() => {
											track('moreThan1CustomizablePage');
											navigate(PATHS.OTO_MODULES);
										}}
									/>
								</div>
							}>
							<Button
								icon={faPlus}
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
								label={!isMobile && 'addCustomizablePages'}
								onClick={() => showModal('customizable-page-form')}
								variant="blue"
								className="addButton"
								disabled={isAddButtonDisabled}
							/>
						</Tooltip>
					) : (
						<Button
							icon={faPlus}
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
							label={!isMobile && 'addCustomizablePages'}
							onClick={() => showModal('customizable-page-form')}
							variant="blue"
							className="addButton"
							disabled={isAddButtonDisabled}
						/>
					)}
				</Authorization>
			</PageHeader>

			<DynamicList
				id={DYNAMICLISTS.CUSTOMIZABLE_PAGES}
				defaultSort={defaultSort}
				columns={columns}
				fetchUrl={BRAND.GET_CUSTOMIZABLE_PAGES_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="customizablePages"
			/>
		</div>
	);
};

export default CustomizablePagesList;
